import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Slider from 'react-slick';
import { RxUtils, Tr, TypeUtils } from '@utils';
import { useService } from '@hooks';
import {
	carListBandModelService,
	CarModel,
	fleetVehicleService,
	notificationService,
	NotificationType,
	simulationService,
	SimulationTasksGroupedByCarListBand
} from '@services';
import {
	Button,
	Card,
	CardProps,
	Container,
	Divider,
	Grid,
	IconButton,
	MenuItem,
	Stack,
	SxProps,
	TextField,
	Typography
} from '@mui/material';
import { GuardedRender, VehicleImage } from '@components/portal/common';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CircleIcon from '@mui/icons-material/Circle';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';

const tkey = (key: string) => `conversion-list.vehicle-comparison.${key}`;

interface ISelectableModelCardProps extends CardProps {
	model: CarModel;
}

const SelectableModelCard: FC<ISelectableModelCardProps> = ({ model, sx, ...props }) => {
	return (
		<Card
			variant="outlined"
			sx={{
				width: 1.0,
				height: 80,
				paddingX: 1,
				paddingY: 2,
				...sx
			}}
			{...props}
		>
			<Stack direction="row" spacing={2} alignItems="center" sx={{ height: 1.0 }}>
				<VehicleImage model={model} style={{ height: '100%', width: 'auto' }} containerProps={{ sx: { height: 1.0 } }} />
				<Typography variant="body2" sx={{ fontWeight: 600 }}>
					{[model.brand, model.model].filter(Boolean).join(' ')}
				</Typography>
			</Stack>
		</Card>
	);
};

export const CarBandListAssignments: FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation('portal');
	const { id: bandListId = '' } = useParams<{ id: string }>();
	const [activeSlide, setActiveSlide] = useState(0);
	const [sliderRef, setSliderRef] = useState<Slider>();
	const [taskGroup, setTaskGroup] = useState<SimulationTasksGroupedByCarListBand>();
	const [assignments, setAssignments] = useState<Record<string, number | undefined>>({});
	const { data: simulationTasks = [] } = useService(() => simulationService.getLatestSimulationTasksGroupByCarListBand(), []);
	const { data: carListBandModels = [] } = useService(() => carListBandModelService.getAll(bandListId), [bandListId]);

	useEffect(() => setTaskGroup(simulationTasks.find(({ carListBand }) => carListBand?.id === bandListId)), [simulationTasks]);
	useEffect(
		() =>
			setAssignments(
				(taskGroup?.tasks || [])
					.filter(({ fleetVehicle }) => fleetVehicle)
					.reduce(
						(acc, { fleetVehicle, taskVehicles }) => ({
							...acc,
							[fleetVehicle?.id]: taskVehicles.find(
								(proposedVehicle) => !!carListBandModels.find((model) => model.carModelId === proposedVehicle.carModelId)
							)?.carModelId
						}),
						{}
					)
			),
		[taskGroup]
	);

	return (
		<Stack direction="column" sx={{ height: 1.0, overflowY: 'hidden' }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ paddingX: 6.5, minHeight: 88 }}>
				<Typography variant="h3" sx={{ fontWeight: 'bold' }}>
					<Tr.Portal path={tkey('car-band-list-assignments.title')} options={{ label: taskGroup?.carListBand?.label || '' }} />
				</Typography>
				<IconButton sx={{ color: 'common.black' }} onClick={() => navigate(-2)}>
					<CloseIcon color="inherit" fontSize="large" />
				</IconButton>
			</Stack>
			<Divider sx={{ opacity: 0.4 }} />
			{taskGroup ? (
				<>
					<Container maxWidth="lg" sx={{ flexGrow: 1, overflowY: 'hidden' }}>
						<Stack direction="column" sx={{ height: 1.0 }}>
							<Stack direction="row" alignItems="center" spacing={6} sx={{ paddingY: 3 }}>
								<IconButton
									sx={{ backgroundColor: 'common.black', '&.Mui-disabled': { backgroundColor: 'grey.100' } }}
									disabled={carListBandModels.length <= 3}
									disableRipple
									onClick={() => sliderRef?.slickPrev()}
								>
									<ArrowBackIosNewIcon sx={{ color: 'common.white', width: 12, height: 12 }} />
								</IconButton>

								{carListBandModels.length <= 3 ? (
									<Grid container direction="row" columnSpacing={2}>
										{carListBandModels
											.filter(({ carModel }) => carModel)
											.map(({ id, carModel }, i) => {
												return (
													<Grid item key={`model-${id}-${i}`} xs={4}>
														<SelectableModelCard model={carModel} />
													</Grid>
												);
											})}
									</Grid>
								) : (
									<div style={{ width: `calc(100% - ${(28 + 6 * 8) * 2}px)` }}>
										<Slider
											ref={(ref) => ref && !sliderRef && setSliderRef(ref)}
											dots
											arrows={false}
											draggable
											infinite
											speed={500}
											slidesToShow={3}
											slidesToScroll={3}
											afterChange={setActiveSlide}
											customPaging={(i) => {
												const sx: SxProps = { width: 12, height: 12, color: 'common.black' };
												return (
													<IconButton key={`slider-nav-${i}`} disableRipple>
														{i === Math.round(activeSlide / 3) ? (
															<CircleIcon sx={sx} />
														) : (
															<CircleOutlinedIcon sx={sx} />
														)}
													</IconButton>
												);
											}}
										>
											{carListBandModels
												.filter(({ carModel }) => carModel)
												.map(({ id, carModel }, i) => {
													return (
														<SelectableModelCard
															key={`model-${id}-${i}`}
															model={carModel}
															sx={{
																width: `calc(100% - 32px) !important`,
																marginX: 2
															}}
														/>
													);
												})}
										</Slider>
									</div>
								)}

								<IconButton
									sx={{ backgroundColor: 'common.black', '&.Mui-disabled': { backgroundColor: 'grey.100' } }}
									disabled={carListBandModels.length <= 3}
									disableRipple
									onClick={() => sliderRef?.slickNext()}
								>
									<ArrowForwardIosIcon sx={{ color: 'common.white', width: 12, height: 12 }} />
								</IconButton>
							</Stack>

							<Stack direction="column" spacing={1} sx={{ flexGrow: 1, overflowY: 'auto', paddingY: 3 }}>
								{taskGroup.tasks.map(({ id, fleetVehicle }, i) => {
									return (
										<Card
											key={`task-${id}-${i}`}
											variant="outlined"
											sx={{ paddingX: 6, paddingY: 3, overflow: 'unset' }}
										>
											<Grid container direction="row" columns={7}>
												<Grid item xs={2}>
													<Stack direction="column">
														<Typography variant="body2" fontWeight={400}>
															<Tr.Portal path="conversion-list.vehicle-comparison.car-band-list-assignments.name" />
														</Typography>
														<GuardedRender
															canRender={(u) => !u.isSellerLoggedInAsManager()}
															fallback={<Typography>-</Typography>}
														>
															<Typography fontWeight={400}>
																{[fleetVehicle?.driverName, fleetVehicle?.driverSurname]
																	.filter(Boolean)
																	.join(' ')}
															</Typography>
														</GuardedRender>
													</Stack>
												</Grid>
												<Grid item xs={2}>
													<Stack direction="column">
														<Typography variant="body2" fontWeight={400}>
															<Tr.Portal path="conversion-list.vehicle-comparison.car-band-list-assignments.current-vehicle" />
														</Typography>
														<Typography fontWeight={400}>
															{[
																[fleetVehicle?.carModel?.brand, fleetVehicle?.carModel?.model]
																	.filter(Boolean)
																	.join(' '),
																fleetVehicle?.plateNumber
															]
																.filter(Boolean)
																.join(' - ')}
														</Typography>
													</Stack>
												</Grid>
												<Grid item xs={3}>
													<TextField
														select
														placeholder={t(tkey('car-band-list-assignments.select-ev'))}
														fullWidth
														name="selectEv"
														SelectProps={{
															displayEmpty: true,
															renderValue: (selected: any) => {
																const carModel = selected
																	? carListBandModels.find(
																			({ carModelId }) => `${carModelId}` === `${selected}`
																	  )?.carModel
																	: undefined;
																return selected ? (
																	[carModel?.brand, carModel?.model].filter(Boolean).join(' ')
																) : (
																	<Typography sx={{ color: 'grey.800', fontWeight: 400 }}>
																		<Tr.Portal path={tkey('car-band-list-assignments.select-ev')} />
																	</Typography>
																);
															}
														}}
														value={assignments[fleetVehicle.id] || ''}
														onChange={(e) =>
															setAssignments({ ...assignments, [fleetVehicle.id]: +e.target.value })
														}
													>
														{(carListBandModels || [])
															.filter(({ carModel }) => carModel)
															.map(({ carModel }, j) => (
																<MenuItem key={`vehicle-${carModel.id}-${i}-${j}`} value={carModel.id}>
																	{[carModel.brand, carModel.model].filter(Boolean).join(' ')}
																</MenuItem>
															))}
													</TextField>
												</Grid>
											</Grid>
										</Card>
									);
								})}
							</Stack>
						</Stack>
					</Container>
					<Divider />
					<Stack direction="row" alignItems="center" justifyContent="center" spacing={3} sx={{ paddingY: 5 }}>
						<Button
							data-testid="conversion-list-car-band-list-assignment-assign-button"
							variant="contained"
							color="secondary"
							disabled={Object.values(assignments).filter((val) => !TypeUtils.isDefined(val)).length > 0}
							onClick={() =>
								RxUtils.promisify(
									fleetVehicleService.selectSolutions(
										Object.keys(assignments).map((vehicleId) => ({
											id: vehicleId,
											selectedCarModelId: assignments[vehicleId] || 0
										}))
									),
									() => {
										notificationService.send({
											type: NotificationType.SUCCESS,
											message: t(tkey('car-band-list-assignments.assignments-succesfull'), {
												label: taskGroup?.carListBand?.label || ''
											})
										});
										navigate(-2);
									},
									() => RxUtils.noop
								)
							}
						>
							<Tr.Portal path="assign" />
						</Button>
						<Button
							data-testid="conversion-list-car-band-list-assignment-back-button"
							variant="outlined"
							color="secondary"
							onClick={() => navigate(-1)}
						>
							<Tr.Portal path="back" />
						</Button>
					</Stack>
				</>
			) : null}
		</Stack>
	);
};
